

import { defineComponent, ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { stockpool } from '@/api/index'
import { QUANT_GOLD_STAT_PERIODS } from './quantGoldMock'



interface QuantGoldHoldPeriodRow {

  period: string

  successRate: number | null

  holdDays: number | null

  avgIncrease: number | null

}



interface QuantGoldModelReturnRow {

  period: string

  annualExcess: number | null

  sharpeRatio: number | null

  profitLossRatio: number | null

}



export default defineComponent({

  name: 'QuantGoldBacktest',

  props: {

    theme: { type: String, default: 'light' }

  },

  setup(props) {

    const route = useRoute()

    const loading = ref(false)

    const palette = () => (
      props.theme === 'dark'
        ? { red: '#ff5c5c', green: '#39e365', gray: '#959ead', default: '#e0e0e0' }
        : { red: '#cc2b1d', green: '#439731', gray: '#959ead', default: '#333' }
    )

    const holdPeriodData = ref<QuantGoldHoldPeriodRow[]>(

      QUANT_GOLD_STAT_PERIODS.map(p => ({

        period: p.label,

        successRate: null,

        holdDays: null,

        avgIncrease: null

      }))

    )

    const modelReturnData = ref<QuantGoldModelReturnRow[]>(

      QUANT_GOLD_STAT_PERIODS.map(p => ({

        period: p.label,

        annualExcess: null,

        sharpeRatio: null,

        profitLossRatio: null

      }))

    )



    const getRequestHeaders = () => ({

      clienttype: 'pc',

      appVersion: route.query.appVersion,

      token: route.query.token,

      'Content-Type': 'application/json'

    })



    const getTrackPayload = (res: any) => {

      if (!res) return null

      if (res.successRate != null || res.avgHoldDays != null || res.avgRangeRate != null) return res

      return res.data ?? null

    }



    const fetchData = async () => {

      loading.value = true

      try {

        const results = await Promise.all(

          QUANT_GOLD_STAT_PERIODS.map(item =>

            stockpool.getQuantGoldTrack({

              data: { period: item.value },

              headers: getRequestHeaders()

            })

          )

        )

        results.forEach((res: any, index: number) => {

          const data = getTrackPayload(res)

          if (!data) return

          holdPeriodData.value[index] = {

            period: QUANT_GOLD_STAT_PERIODS[index].label,

            successRate: data.successRate ?? null,

            holdDays: data.avgHoldDays ?? null,

            avgIncrease: data.avgRangeRate ?? null

          }

          modelReturnData.value[index] = {

            period: QUANT_GOLD_STAT_PERIODS[index].label,

            annualExcess: data.annualExcessReturn ?? null,

            sharpeRatio: data.sharpeRatio ?? null,

            profitLossRatio: data.profitLossRatio ?? null

          }

        })

      } catch (e) {

        console.error('QuantGoldBacktest fetchData failed', e)

      } finally {

        loading.value = false

      }

    }



    onMounted(fetchData)



    const getPopupContainer = (trigger: HTMLElement) => trigger.parentElement || document.body



    const formatPercent = (val: number | null | undefined) => {

      if (val === null || val === undefined) return '--'

      return (val * 100).toFixed(2) + '%'

    }



    const formatDays = (val: number | null | undefined) => {

      if (val === null || val === undefined) return '--'

      return val.toFixed(1) + '天'

    }



    const formatSharpe = (val: number | null | undefined) => {

      if (val === null || val === undefined) return '--'

      return val.toFixed(2)

    }



    const formatProfitLossRatio = (val: number | null | undefined) => {

      if (val === null || val === undefined) return '--'

      return val.toFixed(2)

    }



    /** 红涨绿跌：>0 红，<0 绿，=0/空 灰 */
    const getChangeColor = (val: number | null | undefined) => {
      const colors = palette()
      if (val === null || val === undefined || val === 0) return colors.gray
      return val > 0 ? colors.red : colors.green
    }

    /** 持有周期：有值固定红，空值灰 */
    const getHoldDaysColor = (val: number | null | undefined) => {
      const colors = palette()
      if (val === null || val === undefined) return colors.gray
      return colors.red
    }

    /** 夏普比率：>1 红，<0 绿，0~1 默认色 */
    const getSharpeColor = (val: number | null | undefined) => {
      const colors = palette()
      if (val === null || val === undefined) return colors.gray
      if (val > 1) return colors.red
      if (val < 0) return colors.green
      return colors.default
    }

    /** 盈亏比（与 1 比）：>1 红，<1 绿，=1/空 灰 */
    const getVsOneColor = (val: number | null | undefined) => {
      const colors = palette()
      if (val === null || val === undefined) return colors.gray
      if (val > 1) return colors.red
      if (val < 1) return colors.green
      return colors.gray
    }

    return {

      loading,

      holdPeriodData,

      modelReturnData,

      getPopupContainer,

      formatPercent,

      formatDays,

      formatSharpe,

      formatProfitLossRatio,

      getChangeColor,

      getHoldDaysColor,

      getSharpeColor,

      getVsOneColor

    }

  }

})

